import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Card,
  CommandResult,
  EmoteKind,
  JoinResult,
  LobbyView,
  RoomView,
  SessionCredentials,
} from "../game/types";
import { deviceKey } from "./identity";
import { recordMatch } from "./stats";
import { socket } from "./socket";

const SESSION_KEY = "chin:session:v1";
const EMOTE_VISIBLE_MS = 2_600;

export type ConnectionStatus = "connecting" | "online" | "offline";

export interface Notice {
  id: number;
  kind: "info" | "success" | "warning";
  message: string;
}

export interface EmoteEvent {
  id: number;
  playerId: string;
  emote: EmoteKind;
}

interface OptimisticPlay {
  cardId: string;
  pileIndex: 0 | 1;
  card: Card;
}

function readSession(): SessionCredentials | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionCredentials>;
    if (!parsed.roomCode || !parsed.playerId || !parsed.sessionToken) return null;
    return parsed as SessionCredentials;
  } catch {
    return null;
  }
}

function writeSession(session: SessionCredentials | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private browsing and storage quotas must not prevent a match.
  }
}

function commandId(): string {
  return crypto.randomUUID();
}

/** RTT más reciente, para adaptar el timeout de reintento a la red real. */
let lastLatencyMs: number | null = null;

function retryTimeoutMs(): number {
  if (lastLatencyMs === null) return 1_200;
  return Math.min(1_200, Math.max(250, lastLatencyMs * 3));
}

function reliableCommand(
  send: (acknowledge: (result: CommandResult) => void) => void,
  receive: (result: CommandResult) => void,
  attemptsLeft = 2,
): void {
  let settled = false;
  const timer = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    if (attemptsLeft > 0) {
      reliableCommand(send, receive, attemptsLeft - 1);
      return;
    }
    receive({ ok: false, code: "ACK_TIMEOUT", message: "La red no confirmó la jugada. Probá otra vez." });
  }, retryTimeoutMs());

  send((result) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    receive(result);
  });
}

function selfRackHas(room: RoomView, cardId: string): boolean {
  const self = room.players.find((player) => player.id === room.selfId);
  return self?.rack.some((card) => card?.id === cardId) ?? false;
}

/**
 * Pinta las jugadas aún no confirmadas por el servidor: la carta sale del rack
 * y aparece como tope de la pila al instante. El estado del servidor sigue
 * siendo la única verdad; esta capa se descarta sola cuando llega.
 */
function applyOptimism(room: RoomView | null, plays: OptimisticPlay[]): RoomView | null {
  if (!room || plays.length === 0 || room.phase !== "playing") return room;
  const self = room.players.find((player) => player.id === room.selfId);
  if (!self) return room;

  const rack = [...self.rack];
  const piles: RoomView["piles"] = [{ ...room.piles[0] }, { ...room.piles[1] }];
  let changed = false;

  for (const play of plays) {
    const rackIndex = rack.findIndex((card) => card?.id === play.cardId);
    if (rackIndex === -1) continue;
    rack[rackIndex] = null;
    piles[play.pileIndex] = { top: play.card, count: piles[play.pileIndex].count + 1 };
    changed = true;
  }
  if (!changed) return room;

  const chinAvailable =
    piles[0].top !== null && piles[1].top !== null && piles[0].top.rank === piles[1].top.rank;

  return {
    ...room,
    piles,
    chinAvailable,
    players: room.players.map((player) => (player.id === self.id ? { ...player, rack } : player)),
  };
}

export function useGame() {
  const [serverRoom, setServerRoom] = useState<RoomView | null>(null);
  const [optimisticPlays, setOptimisticPlays] = useState<OptimisticPlay[]>([]);
  const [connection, setConnection] = useState<ConnectionStatus>("connecting");
  const [latency, setLatency] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [emotes, setEmotes] = useState<EmoteEvent[]>([]);
  const [lobby, setLobby] = useState<LobbyView | null>(null);
  const [seeking, setSeeking] = useState(false);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef<SessionCredentials | null>(readSession());
  const roomRef = useRef<RoomView | null>(null);
  const noticeSequence = useRef(0);
  const emoteSequence = useRef(0);

  const room = useMemo(() => applyOptimism(serverRoom, optimisticPlays), [serverRoom, optimisticPlays]);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  const showNotice = useCallback((message: string, kind: Notice["kind"] = "warning") => {
    noticeSequence.current += 1;
    setNotice({ id: noticeSequence.current, kind, message });
  }, []);

  const handleState = useCallback((nextRoom: RoomView) => {
    setServerRoom((previous) => {
      if (previous && nextRoom.phase === "playing" && previous.phase === "playing") {
        for (const player of nextRoom.players) {
          const before = previous.players.find((item) => item.id === player.id);
          if (before && !before.aidReceived && player.aidReceived) {
            showNotice(
              player.id === nextRoom.selfId
                ? "Te quedaste sin mazo: tu rival te cedió una carta."
                : "Tu rival se quedó sin mazo: le cediste una carta.",
              "info",
            );
          }
        }
      }
      if (previous?.phase !== "playing" && nextRoom.phase === "playing") setNotice(null);
      return nextRoom;
    });
    if (nextRoom.phase === "finished") recordMatch(nextRoom);
    setOptimisticPlays((plays) => plays.filter((play) => selfRackHas(nextRoom, play.cardId)));
  }, [showNotice]);

  useEffect(function subscribeToRealtimeServer() {
    function resumeSession() {
      const session = sessionRef.current;
      if (!session) return;

      socket.emit("room:resume", session, (result) => {
        if (result.ok && result.state) {
          handleState(result.state);
          return;
        }

        sessionRef.current = null;
        writeSession(null);
        setServerRoom(null);
        setOptimisticPlays([]);
        if (result.message) showNotice(result.message);
      });
    }

    function handleConnect() {
      setConnection("online");
      resumeSession();
    }

    function handleDisconnect() {
      setConnection("offline");
    }

    function handleConnectError() {
      setConnection("offline");
    }

    function handleNotice(nextNotice: Omit<Notice, "id">) {
      showNotice(nextNotice.message, nextNotice.kind);
    }

    function handleFeedback(result: CommandResult) {
      if (!result.ok && result.message) showNotice(result.message);
    }

    function handleEmote(payload: { playerId: string; emote: EmoteKind }) {
      emoteSequence.current += 1;
      const event: EmoteEvent = { id: emoteSequence.current, ...payload };
      setEmotes((current) => [...current.slice(-3), event]);
      window.setTimeout(() => {
        setEmotes((current) => current.filter((item) => item.id !== event.id));
      }, EMOTE_VISIBLE_MS);
    }

    function handleLobbyUpdate(view: LobbyView) {
      setLobby(view);
    }

    function handleSeekMatched(result: JoinResult) {
      setSeeking(false);
      if (result.ok && result.session && result.state) {
        sessionRef.current = result.session;
        writeSession(result.session);
        setOptimisticPlays([]);
        setServerRoom(result.state);
        showNotice("¡Rival encontrado!", "success");
      }
    }

    function handleSeekExpired() {
      setSeeking(false);
      showNotice("Nadie tomó tu mesa. Probá de nuevo cuando quieras.", "info");
    }

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("room:state", handleState);
    socket.on("room:notice", handleNotice);
    socket.on("room:emote", handleEmote);
    socket.on("lobby:update", handleLobbyUpdate);
    socket.on("seek:matched", handleSeekMatched);
    socket.on("seek:expired", handleSeekExpired);
    socket.on("game:feedback", handleFeedback);
    socket.connect();

    return function unsubscribeFromRealtimeServer() {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("room:state", handleState);
      socket.off("room:notice", handleNotice);
      socket.off("room:emote", handleEmote);
      socket.off("lobby:update", handleLobbyUpdate);
      socket.off("seek:matched", handleSeekMatched);
      socket.off("seek:expired", handleSeekExpired);
      socket.off("game:feedback", handleFeedback);
    };
  }, [handleState, showNotice]);

  useEffect(function subscribeToLobbyWhileIdle() {
    if (connection !== "online" || room !== null) return;
    socket.emit("lobby:subscribe", (view) => setLobby(view));
  }, [connection, room]);

  useEffect(function measureRoundTripTime() {
    function ping() {
      if (!socket.connected) return;
      const startedAt = performance.now();
      socket.emit("network:ping", Date.now(), () => {
        const rtt = Math.round(performance.now() - startedAt);
        lastLatencyMs = rtt;
        setLatency(rtt);
      });
    }

    ping();
    const timer = window.setInterval(ping, 3_000);
    return function stopMeasuringRoundTripTime() {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(function dismissNotices() {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3_200);
    return function clearNoticeTimer() {
      window.clearTimeout(timer);
    };
  }, [notice]);

  const acceptJoin = useCallback((result: JoinResult): JoinResult => {
    setBusy(false);
    if (result.ok && result.session && result.state) {
      sessionRef.current = result.session;
      writeSession(result.session);
      setOptimisticPlays([]);
      setSeeking(false);
      setServerRoom(result.state);
    } else if (result.message) {
      showNotice(result.message);
    }
    return result;
  }, [showNotice]);

  const createRoom = useCallback((name: string, decks: 1 | 2 = 1): Promise<JoinResult> => {
    setBusy(true);
    return new Promise((resolve) => {
      socket.emit("room:create", { name, decks, identity: deviceKey() ?? undefined }, (result) => resolve(acceptJoin(result)));
    });
  }, [acceptJoin]);

  const joinRoom = useCallback((code: string, name: string): Promise<JoinResult> => {
    setBusy(true);
    return new Promise((resolve) => {
      socket.emit("room:join", { code, name, identity: deviceKey() ?? undefined }, (result) => resolve(acceptJoin(result)));
    });
  }, [acceptJoin]);

  const quickMatch = useCallback((name: string, decks: 1 | 2 = 1) => {
    setBusy(true);
    socket.emit("lobby:quick", { name, decks, identity: deviceKey() ?? undefined }, (result) => {
      setBusy(false);
      if (!result.ok) {
        if (result.message) showNotice(result.message);
        return;
      }
      if (result.waiting) {
        setSeeking(true);
        return;
      }
      acceptJoin(result);
    });
  }, [acceptJoin, showNotice]);

  const takeSeek = useCallback((seekId: string, name: string) => {
    setBusy(true);
    socket.emit("seek:take", { seekId, name, identity: deviceKey() ?? undefined }, (result) => {
      setBusy(false);
      acceptJoin(result);
    });
  }, [acceptJoin]);

  const cancelSeek = useCallback(() => {
    setSeeking(false);
    socket.emit("seek:cancel", () => undefined);
  }, []);

  const ready = useCallback(() => {
    const payload = { commandId: commandId() };
    reliableCommand((acknowledge) => socket.emit("room:ready", payload, acknowledge), (result) => {
      if (!result.ok && result.message) showNotice(result.message);
    });
  }, [showNotice]);

  const play = useCallback((cardId: string, pileIndex: 0 | 1) => {
    const currentRoom = roomRef.current;
    const top = currentRoom?.piles[pileIndex].top;
    const self = currentRoom?.players.find((player) => player.id === currentRoom.selfId);
    const card = self?.rack.find((item) => item?.id === cardId) ?? null;
    if (!currentRoom || !top || !card) return;

    setOptimisticPlays((plays) => [...plays, { cardId, pileIndex, card }]);
    const payload = { commandId: commandId(), cardId, pileIndex, expectedTopCardId: top.id };
    reliableCommand(
      (acknowledge) => socket.emit("game:play", payload, acknowledge),
      (result) => {
        if (!result.ok) {
          setOptimisticPlays((plays) => plays.filter((item) => item.cardId !== cardId));
          if (result.message) showNotice(result.message);
        }
      },
    );
  }, [showNotice]);

  const chin = useCallback(() => {
    const currentRoom = roomRef.current;
    if (!currentRoom) return;
    const payload = {
      commandId: commandId(),
      revision: currentRoom.revision,
      seenTopCardIds: [
        currentRoom.piles[0].top?.id ?? null,
        currentRoom.piles[1].top?.id ?? null,
      ] as [string | null, string | null],
    };
    reliableCommand(
      (acknowledge) => socket.emit("game:chin", payload, acknowledge),
      (result) => {
        if (!result.ok && result.message) showNotice(result.message);
      },
    );
  }, [showNotice]);

  const refresh = useCallback(() => {
    const payload = { commandId: commandId() };
    reliableCommand((acknowledge) => socket.emit("game:refresh", payload, acknowledge), (result) => {
      if (!result.ok && result.message) showNotice(result.message);
    });
  }, [showNotice]);

  const sendEmote = useCallback((emote: EmoteKind) => {
    socket.emit("room:emote", { emote }, (result) => {
      if (!result.ok && result.message) showNotice(result.message, "info");
    });
  }, [showNotice]);

  const leave = useCallback(() => {
    socket.emit("room:leave", () => undefined);
    sessionRef.current = null;
    writeSession(null);
    setServerRoom(null);
    setOptimisticPlays([]);
    const url = new URL(window.location.href);
    url.searchParams.delete("sala");
    window.history.replaceState({}, "", url);
  }, []);

  return {
    room,
    connection,
    latency,
    notice,
    emotes,
    lobby,
    seeking,
    busy,
    createRoom,
    joinRoom,
    quickMatch,
    takeSeek,
    cancelSeek,
    ready,
    play,
    chin,
    refresh,
    sendEmote,
    leave,
    dismissNotice: () => setNotice(null),
    showNotice,
  };
}
